import React, { Fragment, useState, useEffect } from "react";

import { func, bool, object, arrayOf, number, any } from "prop-types";
import { connect } from "react-redux";
import history from "../../history";

import { sortBy } from "../../helpers/utils";
import * as style from "./styles.module.scss";

import {
  timesheetRequestUpdate,
  clearErrors,
  timesheetsRequestData,
} from "../../redux/actions";
import { TimesheetStatus } from "../../helpers/timesheets";
import { PulseLoader } from "../../common";
import TimesheetTryAgain from "../../components/TimesheetTryAgain";

const hasTimesheetRef =
  (ref) =>
  ({ timesheetRef }) =>
    ref === timesheetRef;

const Timesheets = ({
  children = () => {},
  updateTimesheet,
  isLoading,
  isSingleTimesheet = false,
  timesheetRef = -1,
  timesheets = undefined,
  errors,
  nonAttendanceCodes,
  clearErrorsWithSource,
  requestTimesheets,
}) => {
  const sortedTimesheets = sortBy(timesheets || [], "-endDate");
  const handleTryAgain = () => {
    clearErrorsWithSource("timesheets");
    requestTimesheets();
  };
  const timesheetErrors =
    errors && errors.filter((error) => error.source === "timesheets");
  const hasTimesheets = typeof timesheets === "object" && timesheets.length > 0;
  const isTimesheetsEmpty =
    (!hasTimesheets &&
      !isLoading &&
      timesheetErrors &&
      timesheetErrors.length === 0) ||
    (!hasTimesheets && !isLoading && !timesheetErrors);

  const [timesheetStatus, setTimesheetStatus] = useState(
    TimesheetStatus.Loading
  );
  useEffect(() => {
    if (isLoading) setTimesheetStatus(TimesheetStatus.Loading);
    if (timesheetErrors && timesheetErrors.length > 0)
      setTimesheetStatus(TimesheetStatus.Failed);
    if (hasTimesheets) setTimesheetStatus(TimesheetStatus.Success);
    if (isTimesheetsEmpty) setTimesheetStatus(TimesheetStatus.Empty);
  }, [timesheets, errors, isLoading]);

  switch (timesheetStatus) {
    case TimesheetStatus.Loading:
      return (
        <div
          className={`w-100 ${style.timesheets__loader} d-flex flex-column justify-content-center align-items-center`}
        >
          <PulseLoader />
          <h2 className="mt-2">Collecting your data</h2>
        </div>
      );
    case TimesheetStatus.Success:
      if (isSingleTimesheet) {
        return (
          <Fragment>
            {children({
              isLoading,
              updateTimesheet,
              timesheet:
                timesheets === undefined
                  ? undefined
                  : timesheets.filter(hasTimesheetRef(timesheetRef))[0],
              errors,
              clearErrorsWithSource,
              requestTimesheets,
              handleTryAgain,
              timesheetErrors,
              hasTimesheets,
              nonAttendanceCodes,
            })}
          </Fragment>
        );
      }
      return (
        <Fragment>
          {children({
            isLoading,
            timesheets: sortedTimesheets,
            updateTimesheet,
            requestTimesheets,
            errors,
            clearErrorsWithSource,
            handleTryAgain,
            timesheetErrors,
            hasTimesheets,
            nonAttendanceCodes,
          })}
        </Fragment>
      );

    case TimesheetStatus.Failed:
      return <TimesheetTryAgain handleTryAgain={handleTryAgain} />;
    case TimesheetStatus.Empty:
      if (isSingleTimesheet) {
        return (
          <div
            style={{ paddingTop: "20px", paddingBottom: "20px" }}
            className="container"
          >
            <p>The selected timesheet was not found.</p>
            <div
              style={{
                display: "flex",
                padding: "30px 0",
                justifyContent: "space-between",
              }}
            >
              <button
                onClick={() => history.push("/profile")}
                style={{ backgroundColor: "lightgray" }}
                className="btn"
              >
                Back to My Account
              </button>
            </div>
          </div>
        );
      }
      return (
        <article style={{ padding: "40px" }}>
          <p id="timesheetsEmpty">You don’t have any timesheets yet.</p>
          <p>
            When you start in a role you’ll be able to complete and review your
            timesheets from this section.
          </p>
          <p>
            For more information about timesheets please contact your
            consultant.
          </p>
        </article>
      );

    default:
      return <Fragment>def</Fragment>;
  }
};

Timesheets.propTypes = {
  children: func,
  updateTimesheet: func.isRequired,
  isSingleTimesheet: bool,
  timesheetRef: number,
  isLoading: bool.isRequired,
  timesheets: arrayOf(object),
  nonAttendanceCodes: object,
  errors: any,
  clearErrorsWithSource: func,
  requestTimesheets: func,
};

const mapStateToProps = ({
  core: {
    timesheets: { isLoading, timesheets, nonAttendanceCodes },
    errors,
  },
}) => ({
  isLoading,
  timesheets,
  errors,
  nonAttendanceCodes,
});

const mapDispatchToProps = {
  updateTimesheet: timesheetRequestUpdate,
  clearErrorsWithSource: clearErrors,
  requestTimesheets: timesheetsRequestData,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(Timesheets);
